/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useMemo, useState, useEffect} from 'react'
import {saveAs} from 'file-saver'
import Papa from 'papaparse'
import {KTSVG, useDebounce} from '../../../helpers'
import {useAuth} from '../../../../app/modules/auth/core/Auth'
import {getDevicesByOrganization, getModelDevices} from '../../../../api/devicesApi'
import {UpdateGoggle} from '../../modals/update-goggle/UpdateGoggle'
import {DeleteGoggle} from '../../modals/delete-goggle/DeleteGoggle'
import {UsersListSearchComponent} from '../../../../app/modules/apps/user-management/users-list/components/header/UsersListSearchComponent'
import {UsersListPagination} from '../../../../app/modules/apps/user-management/users-list/components/pagination/UsersListPagination'
import dayjs from 'dayjs'
import 'dayjs/locale/it'
import localizedFormat from 'dayjs/plugin/localizedFormat'

import './CustomWidget2_2.css'

dayjs.extend(localizedFormat)
dayjs.locale('it')

interface MyData {
  ID_Device: number
  Numero_seriale: string | null
  Nome: string | null
  COD_Tipologia_device: number | null
  Modello: string | null
  Android_ID: string | null
  Ultimo_utilizzo: string | null
}

type SortConfig = {
  key: keyof MyData
  direction: 'asc' | 'desc'
}

type Props = {
  className?: string
}

/**
 * Gestione visori (solo lista, senza campo "Stato").
 */
const GestioneVisoriWidget: React.FC<Props> = ({className = ''}) => {
  // ------------------------------------------------------
  // State principali
  // ------------------------------------------------------
  const {activeOrganization} = useAuth()

  const [data, setData] = useState<MyData[]>([])
  const [allData, setAllData] = useState<MyData[]>([])
  const [filteredData, setFilteredData] = useState<MyData[]>([])

  const [searchTerm, setSearchTerm] = useState<string>('')
  const debouncedSearchTerm = useDebounce(searchTerm, 150)

  const [sortConfig, setSortConfig] = useState<SortConfig>({key: 'ID_Device', direction: 'asc'})

  const [currentPage, setCurrentPage] = useState<number>(1)
  const itemsPerPage = 10

  // Modali
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null)
  const [showUpdateGoggle, setShowUpdateGoggle] = useState(false)
  const [showDeleteGoggle, setShowDeleteGoggle] = useState(false)

  // ------------------------------------------------------
  // Fetch devices dell'organizzazione + modelli
  // ------------------------------------------------------
  const fetchData = async (orgId: number) => {
    try {
      const models = await getModelDevices()
      const modelMap: Record<number, string> = {}
      models.forEach((m: any) => {
        modelMap[m.ID_Tipologia_device] = m.Nome
      })

      const response = await getDevicesByOrganization(orgId)
      
      const mapped: MyData[] = response.map((d: any) => ({
        ID_Device: d.ID_Device,
        Numero_seriale: d.Numero_seriale ?? null,
        Nome: d.Nome ?? null,
        Modello: d.COD_Tipologia_device != null ? modelMap[d.COD_Tipologia_device] ?? null : null,
        Android_ID: d.Android_ID ?? null,
        Ultimo_utilizzo: d.Ultimo_utilizzo ? dayjs(d.Ultimo_utilizzo).format('LLL') : null,
      }))

      setAllData(mapped)
      setData(mapped)
    } catch (err) {
      console.error('Errore nel recupero dei visori:', err)
    }
  }

  useEffect(() => {
    if (activeOrganization) {
      fetchData(activeOrganization.ID_Organizzazione)
    }
  }, [activeOrganization])

  // ------------------------------------------------------
  // Filtro di ricerca (debounced)
  // ------------------------------------------------------
  useEffect(() => {
    let temp = [...data]
    if (debouncedSearchTerm) {
      const lower = debouncedSearchTerm.toLowerCase()
      temp = temp.filter((g) =>
        Object.values(g).some((v) => typeof v === 'string' && v?.toLowerCase().includes(lower))
      )
    }
    setFilteredData(temp)
  }, [debouncedSearchTerm, data])

  // ------------------------------------------------------
  // Ordinamento
  // ------------------------------------------------------
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const {key, direction} = sortConfig

      if (key === 'ID_Device') {
        const aVal = Number(a.ID_Device)
        const bVal = Number(b.ID_Device)
        return direction === 'asc' ? aVal - bVal : bVal - aVal
      }

      const aVal = a[key] ?? ''
      const bVal = b[key] ?? ''
      if (aVal < bVal) return direction === 'asc' ? -1 : 1
      if (aVal > bVal) return direction === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredData, sortConfig])

  // ------------------------------------------------------
  // Paginazione
  // ------------------------------------------------------
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    const end = start + itemsPerPage
    return sortedData.slice(start, end)
  }, [sortedData, currentPage])

  // ------------------------------------------------------
  // Utils
  // ------------------------------------------------------
  const changeSort = (key: keyof MyData) => {
    setSortConfig((curr) =>
      curr.key === key ? {key, direction: curr.direction === 'asc' ? 'desc' : 'asc'} : {key, direction: 'asc'}
    )
  }

  const exportDataToCSV = () => {
    const csv = Papa.unparse(filteredData)
    saveAs(new Blob([csv], {type: 'text/csv;charset=utf-8;'}), 'Visori.csv')
  }

  const totalGoggles = useMemo(() => allData.length, [allData])

  // ------------------------------------------------------
  // Gestione modali
  // ------------------------------------------------------
  const handleRowClick = (action: string, id: number) => {
    setSelectedRecordId(id)
    if (action === 'update-goggle') setShowUpdateGoggle(true)
    if (action === 'delete-goggle') setShowDeleteGoggle(true)
  }

  const closeModals = () => {
    setShowUpdateGoggle(false)
    setShowDeleteGoggle(false)
  }

  // ------------------------------------------------------
  // Render
  // ------------------------------------------------------
  return (
    <div className={`card ${className}`}>
      {/* Barra di ricerca + export */}
      <div className='card-header border-0 pt-6'>
        <UsersListSearchComponent setParentSearchTerm={setSearchTerm} />
        <div className='card-toolbar d-flex align-items-center'>
          <div className='me-10'>
            <span className='fw-bold'>TOTALE VISORI:&nbsp;</span>
            {totalGoggles}
          </div>
          <button className='btn btn-light-primary me-3' onClick={exportDataToCSV}>
            <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
            Export
          </button>
        </div>
      </div>

      {/* Header semplice */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Visori</span>
        </h3>
      </div>

      {/* Tabella */}
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
            <thead>
              <tr className='border-0'>
                <th className='p-0 w-0px'></th>
                <th className='p-0 min-w-150px th-padding text-muted fw-bold text-head' onClick={() => changeSort('Numero_seriale')}>
                  N° Seriale
                </th>
                <th className='p-0 min-w-100px th-padding text-muted fw-bold text-head' onClick={() => changeSort('Nome')}>
                  Nome
                </th>
                <th className='p-0 min-w-100px th-padding text-muted fw-bold text-head' onClick={() => changeSort('Modello')}>
                  Modello
                </th>
                {/*<th className='p-0 min-w-100px th-padding text-muted fw-bold text-head' onClick={() => changeSort('Ultimo_utilizzo')}>
                  Ultimo mirroring
                </th>*/}
              </tr>
            </thead>
            <tbody>
              {paginated.map((item) => (
                <tr key={item.ID_Device}>
                  <td></td>
                  <td className='text-body fw-semibold'>{item.Numero_seriale ?? '-'}</td>
                  <td className='text-body fw-semibold'>{item.Nome ?? '-'}</td>
                  <td className='text-body fw-semibold'>{item.Modello ?? '-'}</td>
                  {/*<td className='text-body fw-semibold'>{item.Ultimo_utilizzo ?? '-'}</td>*/}
                  <td className='text-end'>
                    <div className='btn-group'>
                      <button
                        type='button'
                        className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary'
                        data-bs-toggle='dropdown'
                        aria-expanded='false'
                      >
                        <KTSVG path='/media/icons/duotune/general/gen052.svg' className='svg-icon-2' />
                      </button>
                      <ul className='dropdown-menu custom-dropdown-menu'>
                        <li className='menu-item px-3'>
                          <a className='dropdown-item menu-item px-3' onClick={() => handleRowClick('update-goggle', item.ID_Device)}>
                            Modifica
                          </a>
                        </li>
                        <li className='menu-item px-3'>
                          <a className='dropdown-item menu-item px-3' onClick={() => handleRowClick('delete-goggle', item.ID_Device)}>
                            Elimina
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Paginazione */}
        <UsersListPagination totalItems={filteredData.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>

      {/* Modali */}
      {selectedRecordId !== null && (
        <>
          <UpdateGoggle show={showUpdateGoggle} handleClose={closeModals} recordId={selectedRecordId} />
          <DeleteGoggle show={showDeleteGoggle} handleClose={closeModals} recordId={selectedRecordId} />
        </>
      )}
    </div>
  )
}

export {GestioneVisoriWidget}
